import React, { useRef, useState, useEffect } from "react";
import { swalAlertDanger, swalAlertInfo } from "../../components/common/Alerts";
import { postData, getData } from "../../services/api.service";
import FormFilterDate from "../../components/common/FormFilterDate";
import TableDataRender from "../../components/common/TableDataRender";
import { CashCoin, Calendar, Receipt,  } from "react-bootstrap-icons";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import "../../styles/Admin.css";

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const AdminReportsPage = () => {
  const tableRef = useRef();
  const navigate = useNavigate();
  const { id_branch } = useParams();
  const dataUser = useSelector((state) => state.user.dataUser);

  const [currentBranch, setCurrentBranch] = useState(null);
  const [orders, setOrders] = useState([]);
  const [totalBilling, setTotalBilling] = useState(0);
  const [showReports, setShowReports] = useState(false);
  const [loadingBranch, setLoadingBranch] = useState(true);
  
  const ordersCount = orders.length;
  const payMethodNames = {
    1: "Efectivo",
    2: "Transferencia",
  };

  useEffect(() => {
    if (dataUser === null || dataUser.role_id !== 1) {
      navigate("/");
    }
  }, [dataUser, navigate]);

  // Cargar información de la sucursal actual
  useEffect(() => {
    const fetchCurrentBranch = async () => {
      if (!id_branch) {
        navigate("/home/admin_panel");
        return;
      }

      try {
        setLoadingBranch(true);
        const res = await getData("/api/get_branches");
        if (res.status === 200) {
          const branchList = res.data.branches || res.data;
          const branch = branchList.find(b => b.id === parseInt(id_branch));
          
          if (branch) {
            setCurrentBranch(branch);
          } else {
            swalAlertDanger("Error", "Sucursal no encontrada.", () => {
              navigate("/home/admin_panel");
            });
          }
        } else {
          swalAlertDanger("Error", "No se pudo cargar la información de la sucursal.", () => {});
        }
      } catch (error) {
        console.error("Error fetching branch:", error);
        swalAlertDanger("Error", "Error al cargar la sucursal.", () => {});
      } finally {
        setLoadingBranch(false);
      }
    };
    fetchCurrentBranch();
  }, [id_branch, navigate]);

  const handleInfo = async (data) => {
    if (!id_branch) {
      swalAlertInfo("Atención", "No se ha seleccionado una sucursal.", () => {});
      return;
    }

    try {
      const requestData = {
        ...data,
        branch_id: parseInt(id_branch),
      };
      
      const resOrders = await postData("/api/admin/get_report_sales", requestData);
      if (resOrders.status === 200) {
        setOrders(resOrders.data.report);
        setTotalBilling(resOrders.data.total_billing);
        setShowReports(true);
      }
    } catch (error) {
      swalAlertDanger("Error", "No se pudieron cargar los reportes.", () => {});
    }
  };

  const columnMapping = {
    "ID ORDEN": { key: "order_id", dataType: "text", align: "center" },
    FECHA: { key: "inserted_date", dataType: "date", align: "center" },
    CLIENTE: { key: "user_id", dataType: "text", align: "center" },
    "TOTAL ($)": { key: "total_price", dataType: "text", align: "center" },
  };

  const billingByPayMethod = orders.reduce((acc, order) => {
    const method = order.pay_method || "Sin método";
    acc[method] = (acc[method] || 0) + Number(order.total_price || 0);
    return acc;
  }, {});

  // Datos para el gráfico de métodos de pago (Doughnut)
  const payMethodChartData = {
    labels: Object.keys(billingByPayMethod).map(key => payMethodNames[key] || key),
    datasets: [
      {
        label: 'Facturación por método',
        data: Object.values(billingByPayMethod),
        backgroundColor: [
          'rgba(75, 192, 192, 0.8)',
          'rgba(255, 159, 64, 0.8)',
          'rgba(153, 102, 255, 0.8)',
        ],
        borderColor: [
          'rgba(75, 192, 192, 1)',
          'rgba(255, 159, 64, 1)',
          'rgba(153, 102, 255, 1)',
        ],
        borderWidth: 2,
      },
    ],
  };

  // Agrupar pedidos por fecha para el gráfico de línea
  const ordersByDate = orders.reduce((acc, order) => {
    const date = new Date(order.inserted_date).toLocaleDateString('es-AR');
    acc[date] = (acc[date] || 0) + Number(order.total_price || 0);
    return acc;
  }, {});

  const lineChartData = {
    labels: Object.keys(ordersByDate),
    datasets: [
      {
        label: 'Facturación diaria ($)',
        data: Object.values(ordersByDate),
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  // Calcular promedio de facturación por pedido
  const averageOrderValue = ordersCount > 0 ? (totalBilling / ordersCount).toFixed(2) : 0;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#fff',
          font: {
            size: 12
          }
        }
      },
    },
  };

  return (
    <div className="container-fluid mt-4 px-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <button
            className="btn btn-outline-light mb-3"
            onClick={() => navigate(`/home/admin_panel/${id_branch}`)}
          >
            ← Volver al Panel
          </button>
          <h2 className="text-center text-white mb-1">
       
            Reportes de Facturación
          </h2>
          <p className="text-center text-white-50">
            Análisis detallado de ventas y rendimiento
          </p>
        </div>
      </div>

      {/* Filtros */}
      <section className="row mb-4">
        <div className="col-12">
          <div className="card bg-dark border-secondary p-4">
            <h4 className="text-white mb-3">
              <Calendar className="me-2" />
              Filtros de búsqueda
            </h4>
            
            {/* Información de sucursal actual */}
            {loadingBranch ? (
              <div className="text-center py-3">
                <div className="spinner-border text-light" role="status">
                  <span className="visually-hidden">Cargando sucursal...</span>
                </div>
              </div>
            ) : currentBranch ? (
              <>
                <div className="alert alert-info mb-4">
                  <h5 className="mb-1">
                    📍 Sucursal: <strong>{currentBranch.branch_name}</strong>
                  </h5>
                  <p className="mb-0 text-muted">
                    {currentBranch.address}
                  </p>
                </div>

                {/* Filtro de fecha */}
                <div>
                  <label className="form-label text-white fw-bold mb-3">
                    Selecciona el rango de fechas
                  </label>
                  <FormFilterDate handleInfo={handleInfo} />
                </div>
              </>
            ) : (
              <div className="alert alert-warning">
                No se pudo cargar la información de la sucursal.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Reportes */}
      {showReports && (
        <div className="mb-5">
          {/* Cards de métricas */}
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-6 col-xl-3">
              <div className="card bg-gradient text-white h-100 shadow" style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="text-white-50 mb-2">Total Facturado</h6>
                      <h3 className="mb-0 fw-bold">$ {totalBilling.toLocaleString('es-AR')}</h3>
                    </div>
                    <CashCoin size={40} className="opacity-75" />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6 col-xl-3">
              <div className="card bg-gradient text-white h-100 shadow" style={{background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'}}>
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="text-white-50 mb-2">Total de Pedidos</h6>
                      <h3 className="mb-0 fw-bold">{ordersCount}</h3>
                    </div>
                    <Receipt size={40} className="opacity-75" />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6 col-xl-3">
              <div className="card bg-gradient text-white h-100 shadow" style={{background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'}}>
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="text-white-50 mb-2">Promedio por Pedido</h6>
                      <h3 className="mb-0 fw-bold">$ {averageOrderValue}</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6 col-xl-3">
              <div className="card bg-dark text-white h-100 shadow border-secondary">
                <div className="card-body">
                  <h6 className="text-white-50 mb-3">Métodos de Pago</h6>
                  <div className="d-flex flex-column gap-2">
                    {Object.entries(billingByPayMethod).map(([method, total]) => (
                      <div key={method} className="d-flex justify-content-between">
                        <small>{payMethodNames[method] || method}:</small>
                        <small className="fw-bold">${total.toFixed(2)}</small>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Gráficos */}
          <div className="row g-3 mb-4">
            <div className="col-12 col-lg-8">
              <div className="card bg-dark border-secondary p-4 h-100">
                <h5 className="text-white mb-3">Tendencia de Facturación</h5>
                <div style={{ height: '300px' }}>
                  <Line data={lineChartData} options={chartOptions} />
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-4">
              <div className="card bg-dark border-secondary p-4 h-100">
                <h5 className="text-white mb-3">Distribución por Método de Pago</h5>
                <div style={{ height: '300px' }} className="d-flex align-items-center justify-content-center">
                  <Doughnut data={payMethodChartData} options={chartOptions} />
                </div>
              </div>
            </div>
          </div>

          {/* Tabla de pedidos */}
          <div className="card bg-dark border-secondary p-4">
            <h5 className="text-white mb-3">
              <Receipt className="me-2" />
              Detalle de Pedidos
            </h5>
            <TableDataRender
              data={orders}
              columnMapping={columnMapping}
              tableRef={tableRef}
              scroll={true}
            />
          </div>
        </div>
      )}

      {/* Mensaje cuando no hay reportes */}
      {!showReports && currentBranch && !loadingBranch && (
        <div className="text-center py-5">
          <Calendar size={60} className="text-white-50 mb-3" />
          <h4 className="text-white-50">Selecciona un rango de fechas para ver los reportes</h4>
          <p className="text-white-50">Sucursal: {currentBranch.branch_name}</p>
        </div>
      )}
    </div>
  );
};

export default AdminReportsPage;
